/**
 * Phase 23 / Plan 23-05 / BDSEC-04 — Action-Mapping-Pure-Function.
 *
 * Mapped jeden Mollie-Status auf ein konsistentes Set Primary/Secondary
 * Action-Keys gemäß Sketch-Findings (`mollie-status-and-actions.md`, Z.47-56).
 *
 * Status-Vokabular: 1:1 Mollie wire ("canceled" mit 1 l).
 *
 * Action-Keys (sind reine UI-Identifier — keine Mutation-Namen):
 *   resend          — Link erneut senden
 *   copy            — Link kopieren
 *   cancel          — Stornieren
 *   refresh         — Status aktualisieren
 *   captureFull     — Vollständig erfassen
 *   capturePartial  — Teilweise erfassen
 *   refundPartial   — Erstatten (Betrag immer frei eingeben)
 *   refundMore      — Refund weiter (bei paid + partial-refund)
 *   showChargebacks — Chargebacks anzeigen
 *   openDashboard   — Mollie-Dashboard öffnen
 *   newAttempt      — Neuen Zahlungslink erstellen
 *   recordExternalPayment — Außerhalb Mollie bezahlt erfassen (Überweisung/PayPal etc.)
 */

export type MollieActionKey =
    | "resend"
    | "copy"
    | "cancel"
    | "refresh"
    | "captureFull"
    | "capturePartial"
    | "refundPartial"
    | "refundMore"
    | "showChargebacks"
    | "openDashboard"
    | "newAttempt"
    | "recordExternalPayment";

export interface ActionSet {
    primary: ReadonlyArray<MollieActionKey>;
    secondary: ReadonlyArray<MollieActionKey>;
}

const EMPTY_SET: ActionSet = {primary: [], secondary: []};

/**
 * actionMappingForState — pure Funktion, mapped Mollie-Status auf Action-Set.
 *
 * @param status            Mollie wire status ("open" | "pending" | ...).
 * @param hasPartialRefund  Nur relevant bei status === "paid" UND
 *                          isFullyRefunded === false:
 *                          - true  → "paid+partial-refund"-Mapping
 *                                    (Refund weiter / Chargebacks / Dashboard)
 *                          - false → vollständiges "paid"-Mapping
 *                                    (Erstatten / Chargebacks / Dashboard)
 * @param isFullyRefunded   Nur relevant bei status === "paid". Hat Vorrang vor
 *                          `hasPartialRefund`:
 *                          - true  → KEINE Refund-Action mehr (Restbetrag bei
 *                                    Mollie ist 0). Es bleiben nur
 *                                    Chargebacks / Dashboard.
 *                          Hintergrund (Prod-Incident B-104): Bei einer voll
 *                          erstatteten Zahlung würde "Vollständig/Teilweise
 *                          erstatten" beim Klick am Backend-Guard 3c scheitern
 *                          ("max. 0.00 € erstattbar"). Daher Buttons ausblenden.
 *                          Default `false` — bestehende Aufrufer/Tests bleiben
 *                          unverändert.
 */
export function actionMappingForState(
    status: string,
    hasPartialRefund: boolean = false,
    isFullyRefunded: boolean = false,
): ActionSet {
    switch (status) {
        case "open":
            return {
                primary: ["resend"],
                // recordExternalPayment: Mollie-Link evtl. defekt → externe Zahlung
                // (Überweisung/PayPal) direkt an der Rate erfassen.
                secondary: ["copy", "cancel", "recordExternalPayment"],
            };
        case "pending":
            return {
                primary: ["refresh"],
                secondary: [],
            };
        case "authorized":
            return {
                primary: ["captureFull", "capturePartial"],
                secondary: ["cancel"],
            };
        case "paid":
            // "refresh" auch bei paid (W-275): kein Status-Fortschritt mehr
            // moeglich (forward-final), aber der Refresh zieht inzwischen die
            // real im Checkout genutzte Mollie-Methode nach — Reparatur fuer
            // Altdaten, deren Link-Attempt mit "creditcard" geseedet wurde.
            if (isFullyRefunded) {
                // Voll erstattet (Summe effektiver Refunds >= gezahltem Betrag):
                // keine Refund-Action mehr anbieten. Vorrang vor hasPartialRefund.
                return {
                    primary: [],
                    secondary: ["showChargebacks", "openDashboard", "refresh"],
                };
            }
            if (hasPartialRefund) {
                return {
                    primary: ["refundMore"],
                    secondary: ["showChargebacks", "openDashboard", "refresh"],
                };
            }
            return {
                primary: ["refundPartial"],
                secondary: ["showChargebacks", "openDashboard", "refresh"],
            };
        case "failed":
        case "expired":
        case "canceled":
            // Mollie wire value: 1 l (canceled).
            // recordExternalPayment: Link nicht mehr nutzbar → externe Zahlung
            // (Überweisung/PayPal) direkt an der Rate erfassen.
            return {
                primary: ["newAttempt"],
                secondary: ["openDashboard", "recordExternalPayment"],
            };
        default:
            // Defensive fallback: unbekannter Status liefert kein Action-Set,
            // wirft aber auch nicht (Tests dokumentieren das Verhalten).
            return EMPTY_SET;
    }
}
